/**
 * AdminMasterIntakesPage (Iter 9.39b)
 *
 * CRUD for db.master_intakes - the single source of truth for intake
 * questions across pilot_onboarding, subscriber_host, and host_experience
 * scopes. Editing a row here updates the question everywhere.
 */
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Save } from "lucide-react";
import { toast } from "sonner";
import MasterDataTable from "@/components/admin/MasterDataTable";
import PreferenceFieldsEditor from "@/components/admin/PreferenceFieldsEditor";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const CATEGORIES = [
  "hair", "makeup", "lashes", "nails", "culinary", "wellness",
  "travel", "style", "dispensary", "events", "instructors",
];
const SCOPES = [
  { value: "all", label: "All scopes" },
  { value: "subscriber_host", label: "Subscriber host" },
  { value: "pilot_onboarding", label: "Pilot onboarding" },
  { value: "host_experience", label: "Host experience" },
];

export default function AdminMasterIntakesPage() {
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all");
  const [cat, setCat] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (scope !== "all") qs.set("scope", scope);
      if (cat !== "all") qs.set("category", cat);
      const r = await fetch(`${API}/admin/master-intakes?${qs}`, { headers: auth() });
      if (r.ok) { const d = await r.json(); setIntakes(d.intakes || []); }
    } finally { setLoading(false); }
  }, [scope, cat]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "category", label: "Category", render: (r) => <Badge variant="outline" className="capitalize text-[10px]">{r.category}</Badge> },
    { key: "scope", label: "Scope", render: (r) => <Badge variant="outline" className="text-[10px]">{r.scope}</Badge> },
    { key: "schema", label: "# Questions", render: (r) => <span className="text-gray-700">{(r.schema || []).length}</span> },
    { key: "version", label: "Version", render: (r) => <span className="text-gray-500">v{r.version || 1}</span> },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-master-intakes-page">
      <MasterDataTable
        title="Master Intakes"
        icon={FileText}
        description="Single source of truth for every intake question across pilot onboarding, subscriber host signup, and per-experience intake. Editing here updates every downstream consumer."
        loading={loading}
        rows={intakes}
        columns={columns}
        searchFields={["category", "scope"]}
        testid="master-intakes"
        extraFilters={
          <div className="flex items-center gap-2">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-44 text-sm" data-testid="intake-scope-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="w-40 text-sm" data-testid="intake-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
        renderRowDetail={(row) => <IntakeEditor row={row} onSaved={load} />}
        emptyHint="No master intakes yet. Run seed script: `python -m scripts.seed_intakes_and_demos`."
      />
    </div>
  );
}

function IntakeEditor({ row, onSaved }) {
  const [schema, setSchema] = useState(row.schema || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/master-intakes/${row.intake_id}`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ schema, version: (row.version || 1) + 1 }),
      });
      if (r.ok) { toast.success(`Saved. ${row.intake_id} → v${(row.version || 1) + 1}`); onSaved && onSaved(); }
      else toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid={`intake-editor-${row.intake_id}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          <code className="text-[10px]">{row.intake_id}</code> - {schema.length} question{schema.length !== 1 ? "s" : ""}.
          Changes sync to every consumer that reads <code className="text-[10px]">db.master_intakes</code>.
        </p>
        <Button size="sm" onClick={save} disabled={saving} className="bg-purple-600" data-testid={`intake-save-${row.intake_id}`}>
          <Save className="w-3.5 h-3.5 mr-1" />{saving ? "Saving…" : "Save"}
        </Button>
      </div>
      <PreferenceFieldsEditor
        value={schema}
        onChange={setSchema}
        testidPrefix={`intake-${row.intake_id}`}
      />
    </div>
  );
}
